/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useState } from "react";
import { MediaBatchPreparationInitServices } from "../../../../../../service/mediaBatchPrepInitServices";
import {
	BatchCreationPayload,
	BatchCreationSuccessResponse,
} from "../../../types/mediaBatchCreation.types";
import { useTranslation } from "react-i18next";
import { MediaBatchData } from "../../../types/mediaBatchCommon.types";
import { useNavigate } from "react-router-dom";

interface Props {
	creationPayload: BatchCreationPayload;
	onNext: () => void;
	showToast: (message: string, toastType: "success" | "error") => void;
	setBatchId: (batchId: number) => void;
	mediaBatchData: MediaBatchData | null;
	validateFields: () => boolean;

}


function isDisabled(
	creationPayload: BatchCreationPayload,
	mediaBatchData: MediaBatchData | null
): boolean {
	// If the batch ID already exists, the button is never disabled
	if (mediaBatchData?.id) {
		return false;
	}

	return (
		creationPayload.media_master_id === "" ||
		creationPayload.intended_use_id === "" ||
		creationPayload.media_batch_description === ""
	);
}

const MBCFooter = ({
	creationPayload,
	onNext,
	showToast,
	setBatchId, 
	mediaBatchData,
	validateFields,
	
}: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [isSubmitting, setIsSubmitting] = useState(false);
	// const disabled = isDisabled(creationPayload, mediaBatchData);

	const handleNext = async () => {
		if(mediaBatchData?.id) {
			onNext();
			return;
		}
		if (!validateFields()) return;
		setIsSubmitting(true);
		try {
			const response: BatchCreationSuccessResponse =
				await MediaBatchPreparationInitServices.postCreationBatch(
					creationPayload,
				);

			setBatchId(Number(response.data.batch_id));

			showToast(
				response.message || "Media Batch Created Successfully",
				"success",
			);

			setTimeout(() => {
				onNext();
			}, 2500);

		} catch (error: any) {
			showToast(
				error?.response?.data?.message ||
				"An unexpected error occurred",
				"error",
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="fixed bottom-0 left-64 right-0 bg-white border-t border-slate-200 px-10 py-3 flex items-center justify-end gap-12 shrink-0 shadow-[0_-20px_40px_-20px_rgba(0,0,0,0.05)]">
			<div className="flex items-center gap-3 mr-auto">
				<span className="material-symbols-outlined text-emerald-500 font-black">
					verified_user
				</span>
				<div className="flex flex-col">
					<span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] leading-none mb-1">
						{t("mediaBatchInit.creation.authorizationStatusLabel")}
					</span>
					<span className="text-[10px] font-black text-emerald-600 uppercase tracking-wider">
						{t("mediaBatchInit.creation.readyToProceed")}
					</span>
				</div>
			</div>
			<button
				onClick={() => navigate("/mediaPreparationBatch")}
				className="flex items-center gap-2 px-8 py-3.5 text-[11px] font-black text-slate-500 uppercase tracking-widest hover:text-red-500 transition-all border-none rounded-2xl"
			>
				{t("mediaBatchInit.creation.cancelButton")}
			</button>
				<button
					onClick={handleNext}
					disabled={isSubmitting}
					className="bg-[#FF7043] text-white flex items-center text-[11px] gap-2 px-6 py-2.5 rounded-2xl shadow-xl shadow-orange-500/30 font-black uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all border-none disabled:opacity-50"
				>
					{isSubmitting
						? t("mediaBatchInit.creation.submitting")
						: t("mediaBatchInit.creation.submitButton")}

					<span className="material-symbols-outlined">rocket_launch</span>
				</button>
		</div>
	);
};
export default MBCFooter;
